import React, { useState } from 'react';
import { Upload, X, FileText, Check, AlertCircle, Camera } from 'lucide-react';
import { SelfieCapture } from './SelfieCapture';

export interface DocumentType {
  id: string;
  label: string;
  required: boolean;
  description?: string;
  acceptedFormats?: string;
  isSelfie?: boolean;
}

interface UploadedDocument {
  file: File;
  type: string;
  preview?: string;
}

interface DocumentUploadProps {
  documentTypes: DocumentType[];
  onUpload: (documents: { type: string; file: File }[]) => Promise<void>;
  maxSize?: number; // in MB
  acceptedFormats?: string[];
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  documentTypes,
  onUpload,
  maxSize = 5,
  acceptedFormats = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
}) => {
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSelfieCapture, setShowSelfieCapture] = useState(false);
  const [selfieDocType, setSelfieDocType] = useState<string>('');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, docType: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File size must be less than ${maxSize}MB`);
      return;
    }

    // Validate file type
    if (!acceptedFormats.includes(file.type)) {
      setError('Only PDF, JPG, and PNG files are accepted');
      return;
    }

    // Create preview for images
    let preview: string | undefined;
    if (file.type.startsWith('image/')) {
      preview = URL.createObjectURL(file);
    }

    // Check if document type already uploaded
    const existingIndex = documents.findIndex((doc) => doc.type === docType);
    if (existingIndex >= 0) {
      // Replace existing document
      const newDocuments = [...documents];
      newDocuments[existingIndex] = { file, type: docType, preview };
      setDocuments(newDocuments);
    } else {
      // Add new document
      setDocuments([...documents, { file, type: docType, preview }]);
    }
  };

  const handleRemoveDocument = (type: string) => {
    setDocuments(documents.filter((doc) => doc.type !== type));
  };

  const handleOpenSelfieCapture = (docType: string) => {
    setSelfieDocType(docType);
    setShowSelfieCapture(true);
  };

  const handleSelfieCapture = (file: File) => {
    setError('');

    // Create preview for the selfie
    const preview = URL.createObjectURL(file);

    // Check if document type already uploaded
    const existingIndex = documents.findIndex((doc) => doc.type === selfieDocType);
    if (existingIndex >= 0) {
      // Replace existing document
      const newDocuments = [...documents];
      newDocuments[existingIndex] = { file, type: selfieDocType, preview };
      setDocuments(newDocuments);
    } else {
      // Add new document
      setDocuments([...documents, { file, type: selfieDocType, preview }]);
    }

    setShowSelfieCapture(false);
    setSelfieDocType('');
  };

  const handleCancelSelfieCapture = () => {
    setShowSelfieCapture(false);
    setSelfieDocType('');
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // Validate required documents
    const requiredTypes = documentTypes.filter((dt) => dt.required).map((dt) => dt.id);
    const uploadedTypes = documents.map((doc) => doc.type);
    const missingTypes = requiredTypes.filter((type) => !uploadedTypes.includes(type));

    if (missingTypes.length > 0) {
      const missingLabels = documentTypes
        .filter((dt) => missingTypes.includes(dt.id))
        .map((dt) => dt.label)
        .join(', ');
      setError(`Please upload required documents: ${missingLabels}`);
      return;
    }

    setUploading(true);

    try {
      await onUpload(documents.map((doc) => ({ type: doc.type, file: doc.file })));
      setSuccess('Documents uploaded successfully');
      setDocuments([]);
    } catch (err: any) {
      setError(err.message || 'Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  const getDocumentForType = (typeId: string) => {
    return documents.find((doc) => doc.type === typeId);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="document-upload-container">
      <div className="upload-header">
        <h3>Upload Documents</h3>
        <p>
          Upload required documents. Max file size: {maxSize}MB. Accepted formats: PDF, JPG, PNG
        </p>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError('')}>
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="success-message">
          <Check size={20} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')}>
            <X size={16} />
          </button>
        </div>
      )}

      <div className="document-types-list">
        {documentTypes.map((docType) => {
          const uploadedDoc = getDocumentForType(docType.id);

          return (
            <div key={docType.id} className="document-type-item">
              <div className="document-type-info">
                <div className="document-type-header">
                  <h4>
                    {docType.label}
                    {docType.required && <span className="required-badge">Required</span>}
                  </h4>
                </div>
                {docType.description && (
                  <p className="document-description">{docType.description}</p>
                )}
                {docType.acceptedFormats && (
                  <small className="accepted-formats">Accepted: {docType.acceptedFormats}</small>
                )}
              </div>

              <div className="document-upload-actions">
                {!uploadedDoc ? (
                  docType.isSelfie ? (
                    <button
                      type="button"
                      className="upload-button selfie-button"
                      onClick={() => handleOpenSelfieCapture(docType.id)}
                    >
                      <Camera size={18} />
                      <span>Take Selfie</span>
                    </button>
                  ) : (
                    <label className="upload-button">
                      <Upload size={18} />
                      <span>Choose File</span>
                      <input
                        type="file"
                        accept={acceptedFormats.join(',')}
                        onChange={(e) => handleFileSelect(e, docType.id)}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )
                ) : (
                  <div className="uploaded-file-info">
                    <div className="file-details">
                      <FileText size={18} />
                      <div className="file-meta">
                        <span className="file-name">{uploadedDoc.file.name}</span>
                        <span className="file-size">
                          {formatFileSize(uploadedDoc.file.size)}
                        </span>
                      </div>
                      <Check size={18} className="success-icon" />
                    </div>
                    <button
                      className="remove-file-button"
                      onClick={() => handleRemoveDocument(docType.id)}
                      type="button"
                    >
                      <X size={18} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="upload-actions">
        <button
          onClick={handleSubmit}
          className="btn btn-primary"
          disabled={uploading || documents.length === 0}
        >
          {uploading ? 'Uploading...' : `Upload ${documents.length} Document(s)`}
        </button>
      </div>

      {/* Selfie Capture Modal */}
      {showSelfieCapture && (
        <SelfieCapture
          onCapture={handleSelfieCapture}
          onCancel={handleCancelSelfieCapture}
        />
      )}
    </div>
  );
};
